import { formatCredits } from '../../hooks/useCreditPricing';

// =============================================================================
// Confirmação de custo do disparo com voz clonada.
//
// Os números vêm de POST /api/campaigns/estimate-voice, que resolve o público
// REAL e monta o texto contato a contato — inclusive quem tem observação longa,
// que gera um áudio bem maior que a média. O total soma só os textos DISTINTOS,
// espelhando o dedupe do backend (áudio idêntico é gerado uma vez só).
// =============================================================================

export type VoiceCampaignEstimate = {
  contacts: number;
  messages: number;
  uniqueAudios: number;
  totalCredits: number;
  minPerContact: number;
  maxPerContact: number;
  avgPerContact: number;
  tooLong: number;
  maxChars: number;
  balance: number;
  sufficient: boolean;
  matchedByFilters?: number;
  invalidPhones?: number;
  duplicatePhones?: number;
  filterMode?: string;
  tagMatch?: string;
};

interface Props {
  estimate: VoiceCampaignEstimate | null;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function VoiceCampaignCostModal({ estimate, loading, onConfirm, onCancel }: Props) {
  if (!loading && !estimate) return null;

  const deduped = estimate ? estimate.contacts * estimate.messages - estimate.uniqueAudios : 0;
  const spread = estimate && estimate.maxPerContact > estimate.minPerContact;

  return (
    // Este modal abre SOBRE o modal de criação de campanha, que usa zIndex 9999.
    // Uma classe z-[90] ficaria atrás dele e o usuário não veria nada ao clicar
    // em "Criar campanha". Fica acima do modal e abaixo do Toaster.
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 p-4" style={{ zIndex: 10050 }}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">🔊 Custo deste disparo</h2>
          <p className="text-xs text-slate-500">Calculado sobre os contatos reais desta campanha.</p>
        </div>

        {loading || !estimate ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">Calculando o custo real...</div>
        ) : (
          <div className="space-y-3 px-5 py-4">
            <div className="rounded-lg border border-slate-200">
              <Row
                label="Contatos que vão receber"
                value={estimate.contacts.toLocaleString('pt-BR')}
                hint={estimate.filterMode === 'intersection' ? 'Filtros combinados (E)' : 'Filtros somados (OU)'}
              />
              {((estimate.invalidPhones || 0) + (estimate.duplicatePhones || 0)) > 0 && (
                <Row
                  label="Descartados pelos filtros"
                  value={`${((estimate.invalidPhones || 0) + (estimate.duplicatePhones || 0)).toLocaleString('pt-BR')}`}
                  hint={`${estimate.invalidPhones || 0} telefone inválido · ${estimate.duplicatePhones || 0} duplicado — não cobram`}
                />
              )}
              {estimate.messages > 1 && <Row label="Áudios por contato" value={String(estimate.messages)} />}
              <Row
                label="Custo por contato"
                value={spread
                  ? `${formatCredits(estimate.minPerContact)} a ${formatCredits(estimate.maxPerContact)}`
                  : formatCredits(estimate.avgPerContact)}
              />
              {deduped > 0 && (
                <Row
                  label="Áudios reaproveitados"
                  value={`${deduped.toLocaleString('pt-BR')} (não cobram)`}
                  hint="Textos idênticos são gerados uma vez só"
                />
              )}
              <Row label="Gerações cobradas" value={estimate.uniqueAudios.toLocaleString('pt-BR')} />
            </div>

            <div className={`rounded-lg border px-3 py-3 ${estimate.sufficient ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}`}>
              <p className={`text-sm font-bold ${estimate.sufficient ? 'text-amber-800' : 'text-red-700'}`}>
                Total: {formatCredits(estimate.totalCredits)} crédito{estimate.totalCredits !== 1 ? 's' : ''}
              </p>
              <p className={`mt-1 text-[11px] ${estimate.sufficient ? 'text-amber-700' : 'text-red-600'}`}>
                Saldo atual: {formatCredits(estimate.balance)}
                {estimate.sufficient
                  ? ` · restam ${formatCredits(estimate.balance - estimate.totalCredits)} após o disparo`
                  : ' · saldo insuficiente. O disparo vai parar quando os créditos acabarem.'}
              </p>
            </div>

            {spread && (
              <p className="text-[11px] leading-relaxed text-slate-500">
                O custo varia por contato porque as variáveis mudam o tamanho do texto — uma observação longa
                gera um áudio maior e mais caro.
              </p>
            )}

            {estimate.tooLong > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">
                <strong>{estimate.tooLong}</strong> contato{estimate.tooLong > 1 ? 's passam' : ' passa'} de {estimate.maxChars.toLocaleString('pt-BR')} caracteres
                depois de trocar as variáveis e vão falhar no envio. Encurte o texto ou tire a variável de observações.
              </div>
            )}

            <p className="text-[11px] leading-relaxed text-slate-400">
              Previsão. A cobrança acontece áudio a áudio, no envio — se a lista de contatos mudar até lá, o total muda junto.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button onClick={onCancel} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || !estimate}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Confirmar e criar campanha
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0">
      <div className="min-w-0">
        <span className="text-xs text-slate-600">{label}</span>
        {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
      </div>
      <span className="shrink-0 text-xs font-semibold text-slate-900">{value}</span>
    </div>
  );
}
